// src/components/RequireSystemAdmin.js
// Route guard for /system-admin/* — requires the system_admin custom claim.
// If the claim is missing, attempts a one-shot self-heal via /auth/set-custom-claims
// (the API will only grant the claim when the user's email is in
// system_config/admins.admin_emails). This recovers users whose tokens predate a
// fix or who lost the claim by entering customer-portal mode in older builds.

import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../apiConfig';
import { decodeJwtPayload } from '../utils/jwtPayload';

function decodeSystemAdminClaim(token) {
  if (!token) return false;
  try {
    const payload = decodeJwtPayload(token) || {};
    return payload.system_admin === true;
  } catch {
    return false;
  }
}

export default function RequireSystemAdmin({ children }) {
  const { currentUser, loading, getIdToken } = useAuth();
  const [status, setStatus] = useState('checking'); // 'checking' | 'allowed' | 'denied'

  useEffect(() => {
    if (loading) return;
    if (!currentUser) {
      setStatus('denied');
      return;
    }

    let cancelled = false;

    (async () => {
      // Fast path: current cached token already has the claim.
      let token = await getIdToken(false);
      if (decodeSystemAdminClaim(token)) {
        if (!cancelled) setStatus('allowed');
        return;
      }

      // Self-heal: ask the API to mint system-admin-only claims. Succeeds only
      // when the email is in system_config/admins.admin_emails; otherwise 403.
      try {
        const resp = await fetch(`${API_BASE_URL}/api/v1/auth/set-custom-claims`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({}),
        });
        if (resp.ok) {
          const refreshed = await getIdToken(true);
          if (decodeSystemAdminClaim(refreshed)) {
            if (!cancelled) setStatus('allowed');
            return;
          }
        }
      } catch (e) {
        console.warn('RequireSystemAdmin self-heal failed:', e);
      }

      if (!cancelled) setStatus('denied');
    })();

    return () => { cancelled = true; };
  }, [currentUser, loading, getIdToken]);

  if (loading || status === 'checking') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (status === 'denied') {
    return <Navigate to={currentUser ? '/' : '/login'} replace />;
  }

  return <>{children}</>;
}
